import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Button,
  DialogActions,
  Typography,
  IconButton,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import FromToDateField from "components/_refactor/fields/from-to-date";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import StructureFields from "components/_refactor/fields/structure";
import { AttachMoney, Close } from "@mui/icons-material";

const statusOptions = [
  { label: "Ativo", value: "ATIVO" },
  { label: "Inativo", value: "INATIVO" },
];

export default function DialogRules({ open, onClose, rule, typeOptions = [] }) {
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [ruleInput, setRuleInput] = useState({
    name: "",
    type: "",
    percentage: "",
    startDate: "",
    endDate: "",
    status: "",
    obs: "",
  });
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);

  useEffect(() => {
    if (rule) {
      setRuleInput({
        name: rule.Nome,
        type: typeOptions.find(
          (option) => option.value === rule.IdTipoCobranca
        ),
        percentage: rule.Percentual,
        startDate: rule.DataInicioVigenciaFormat,
        endDate: rule.DataFimVigenciaFormat,
        status: rule.Ativo ? statusOptions[0] : statusOptions[1],
        obs: rule.obs,
      });
    }
  }, [rule]);

  const handleCreate = async () => {
    setLoading(true);
    try {
      let body = {
        nome: ruleInput.name,
        idTipoCobranca: ruleInput.type.value,
        percentual: ruleInput.percentage,
        dataInicioVigencia: ruleInput.startDate
          .split(/[-/]/)
          .reverse()
          .join("-"),
        dataFimVigencia: ruleInput.endDate.split(/[-/]/).reverse().join("-"),
        idFranquiaRegraCobranca: rule?.IdFranquiaRegraCobranca,
        status: ruleInput?.status?.value === "ATIVO" ? true : false,
      };

      const idGrupoEconomico = structureFilters?.idGrupoEconomico;
      const idLoja = structureFilters?.idLoja;

      if (!idGrupoEconomico && idLoja === "TODAS") {
        body.aplicarTodasLojas = true;
      } else if (!!idGrupoEconomico && idLoja === "TODAS") {
        body.aplicarTodasLojas = true;
        body.idsGrupoEconomicos = [Number(idGrupoEconomico)];
      } else if (!!idGrupoEconomico && !!idLoja) {
        body.aplicarTodasLojas = false;
        body.idLojas = Array.isArray(idLoja)
          ? idLoja.map(Number)
          : idLoja
          ? [Number(idLoja)]
          : [];
      } else {
        body.idsGrupoEconomicos = [Number(idGrupoEconomico)];
      }

      rule
        ? await Api.put("edit-billing-rule", "franquias", body, true)
        : await Api.post("create-billing-rule", "franquias", body, true);

      snackbar.showMessage(
        rule ? "Regra editada com sucesso" : "Regra criada com sucesso",
        { severity: "success" }
      );
      handleClear();
      onClose();
    } catch (error) {
      snackbar.showMessage("Erro ao criar a regra: " + error, {
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!ruleInput.name) {
      return snackbar.showMessage("Nome da regra é obrigatório", {
        severity: "error",
      });
    }
    if (!ruleInput.type) {
      return snackbar.showMessage("Tipo de regra é obrigatório", {
        severity: "error",
      });
    }
    if (!ruleInput.percentage) {
      return snackbar.showMessage("Percentual é obrigatório", {
        severity: "error",
      });
    }
    if (!ruleInput.startDate) {
      return snackbar.showMessage("Data de início é obrigatório", {
        severity: "error",
      });
    }
    if (!ruleInput.endDate) {
      return snackbar.showMessage("Data de fim é obrigatório", {
        severity: "error",
      });
    }
    if (!ruleInput.status) {
      return snackbar.showMessage("Status é obrigatório", {
        severity: "error",
      });
    }
    if (!structureFilters.idGrupoEconomico && !structureFilters.idLoja) {
      return snackbar.showMessage("Grupo econômico ou loja é obrigatório", {
        severity: "error",
      });
    }
    handleCreate();
  };

  const handleClear = () => {
    setRuleInput({
      name: "",
      type: "",
      percentage: "",
      startDate: "",
      endDate: "",
      status: "",
      obs: "",
    });
    setStructureFilters({
      idGrupoEconomico: "",
      idLoja: "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" color="primary">
            <AttachMoney style={{ marginRight: "12px" }} />
            {rule ? "Editar Regra" : "Criar Nova Regra"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <InputText
              label="Nome da Regra"
              value={ruleInput.name}
              onChange={(e) =>
                setRuleInput({ ...ruleInput, name: e.target.value })
              }
              disabled={loading}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutoCompleteField
              label="Tipo de Regra"
              value={ruleInput.type}
              onChange={(e) => setRuleInput({ ...ruleInput, type: e })}
              disabled={loading}
              required
              options={typeOptions}
              optionLabelKey="label"
              optionIdKey="value"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputText
              label="Percentual"
              value={ruleInput.percentage}
              onChange={(e) =>
                setRuleInput({ ...ruleInput, percentage: e.target.value })
              }
              disabled={loading}
              required
              endAdornment="%"
              type="number"
              props={{
                inputProps: {
                  min: 0,
                  max: 100,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <FromToDateField
              label="Selecione o período"
              disabled={loading}
              initialDate={ruleInput.startDate}
              finalDate={ruleInput.endDate}
              setInitialDate={(value) =>
                setRuleInput((prev) => ({ ...prev, startDate: value }))
              }
              setFinalDate={(value) =>
                setRuleInput((prev) => ({ ...prev, endDate: value }))
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutoCompleteField
              label="Status"
              value={ruleInput.status}
              onChange={(e) => setRuleInput((prev) => ({ ...prev, status: e }))}
              disabled={loading}
              required
              options={statusOptions}
              optionLabelKey="label"
              optionIdKey="value"
            />
          </Grid>
          <StructureFields
            clearFilters={clearFilters}
            onStructureFieldsChange={setStructureFilters}
            disableAll={!!structureLoading.length}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={["store"]}
            hideFields={["brand", "channel", "representative"]}
            handleSearch={() => {}}
            franquias
            defaultEconomicGroup={rule?.IdsGrupoEconomicos}
            defaultStore={rule?.IdLojas}
          />
          <Grid item xs={12}>
            <InputText
              label="Observações"
              value={ruleInput.obs}
              onChange={(e) =>
                setRuleInput({ ...ruleInput, obs: e.target.value })
              }
              disabled={loading}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            handleClear();
            onClose();
          }}
          disabled={loading}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={validateForm}
          disabled={loading}
        >
          {rule ? "Editar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
